import React, { useState, useRef } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TextField from '@mui/material/TextField';
import Tooltip from "@mui/material/Tooltip";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { Typography } from "@mui/material";
import './Filter.scss';
import DraggableDialog from "../../components/Dialogs/DraggableDialog";
import Button from "@mui/material/Button";
import InfoIcon from '@mui/icons-material/Info';
import IconButton from '@mui/material/IconButton';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import axios from 'axios';
import { getCompanyId, getToken, getUser, isValidDate } from '../../Utils/Common';
import sharedContext from "../../contexts/shared-context";

const ReportFilter = ({ processReportApiURL, workflowReportApiURL, classificationReportApiURL, envisionReportApiURL, coreReportApiURL, fetchData, reportType, setReportType, setNotify }) => {

    const [calculatedDate, setCalculatedDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [openInfoDialog, setOpenInfoDialog] = useState(false);
    const [openReportRangeInfoDialog, setOpenReportRangeDialog] = useState(false);
    const [openCalculatedDateDialog, setOpenCalculatedDateDialog] = useState(false);

    const globalCompanyId = React.useContext(sharedContext);

    const calculatedDateRef = useRef(null);
    const toDateRef = useRef(null);
    const fromDateRef = useRef(null);

    const DEFAULT_COMPANY_ID = getCompanyId(); // make company dynamic
    const COMPANY_ID = globalCompanyId && globalCompanyId !== "" ? globalCompanyId : DEFAULT_COMPANY_ID;

    const downloadWorkflowReportApiURL = `/api/companies/${COMPANY_ID}/documents/workflow/report/download?`;
    const downloadProcessReportApiURL = `/api/companies/${COMPANY_ID}/documents/process/report/download?`;
    const downloadClassificationReportApiURL = `/api/companies/${COMPANY_ID}/documents/classification/report/download?`;
    const downloadEnvisionReportApiURL = `/api/companies/${COMPANY_ID}/evDocuments/process/report/download?`;
    const downloadCoreReportApiURL = `api/companies/${COMPANY_ID}/documents/core/report/download?`;

    const filterInstructionsDialogTitle = "Filter Instruction";

    const filterInstructionsTextContent2 = `
    Please enter one row for one filter.

    Use date format as follows: 2018-09-21T10:00:00.000

    Sample Filter:
    companyid=1234
    documentGUID@0001025d-c58c-4cea-a693-1b78aff01027
    `;

    const calculatedDateInstructionsDialogTitle = "Calculated Date";
    const calculatedDateInstructionsTextContent =
        <>
            <div className="dialogContainer">
                <div className="dialogContent">
                    <Typography variant="body1" paragraph="true" style={{ wordWrap: "break-word" }}>
                        Enter date as YYYY-MM-DD.
                    </Typography>

                    <Typography variant="body1" paragraph="true" style={{ wordWrap: "break-word" }}>
                        Enter a date to the Calculated Date field to load reports that were
                        calculated on that day. The Calculated Date may be different
                        from the report ingested date which is selected from the Report
                        Date Range filters.
                    </Typography>
                </div>
            </div>
        </>;

    const reportDateRangeInstructionsDialogTitle = "Report Date Range";
    const reportDateRangeInstructionsTextContent =
        <>
            <div className="dialogContainer">
                <div className="dialogContent">
                    <Typography variant="body1" paragraph="true" style={{ wordWrap: "break-word" }}>
                        Enter date as YYYY-MM-DD.
                    </Typography>

                    <Typography variant="body1" paragraph="true" style={{ wordWrap: "break-word" }}>
                        Enter dates to Report Date Range fields to load reports ingested
                        during the date range selected .The Report Date Range feature
                        may be used to further filter the results from Calculated Date.
                    </Typography>
                </div>
            </div>
        </>;

    const handleInfoDialogClose = () => {
        setOpenInfoDialog(false);
    };

    const handleCalculatedDateDialogClose = () => {
        setOpenCalculatedDateDialog(false);
    };

    const handleReportRangeDialogClose = () => {
        setOpenReportRangeDialog(false);
    };

    const formQueryString = () => {
        let qparams = '';

        let calculateDateVal = calculatedDateRef.current.value;
        let fromDateVal = fromDateRef.current.value;
        let toDateVal = toDateRef.current.value;

        if (calculateDateVal && !isValidDate(calculateDateVal)) {
            let message = "Invalid Calculate Date. Date must be in yyyy-MM-dd format if not blank";

            setNotify({
                isOpen: true,
                message: message,
                severity: 'error'
            });
            return false;
        }

        if (fromDateVal && !isValidDate(fromDateVal)) {
            let message = "Invalid Report From Date. Date must be in yyyy-MM-dd format if not blank";

            setNotify({
                isOpen: true,
                message: message,
                severity: 'error'
            });
            return false;
        }

        if (toDateVal && !isValidDate(toDateVal)) {
            let message = "Invalid Report To Date. Date must be in yyyy-MM-dd format if not blank";

            setNotify({
                isOpen: true,
                message: message,
                severity: 'error'
            });
            return false;
        }

        qparams = qparams + 'calculateDate=' + calculateDateVal + '&fromDate=' + fromDateVal + '&toDate=' + toDateVal;

        // console.log('final qparams:', qparams);
        return qparams;
    }


    const getDownloadApiURL = () => {
        let downloadApiURL;

        if (reportType === 'process') {
            // download process report
            downloadApiURL = downloadProcessReportApiURL;
        } else if (reportType === 'workflow') {
            // download workflow report
            downloadApiURL = downloadWorkflowReportApiURL;
        } else if (reportType === 'classification') {
            // download classification report
            downloadApiURL = downloadClassificationReportApiURL;
        } else if (reportType === 'enVision') {
            // download enVision report
            downloadApiURL = downloadEnvisionReportApiURL;
        } else if (reportType === 'core') {
            // download core report
            downloadApiURL = downloadCoreReportApiURL;
        }

        return downloadApiURL;

    };
    const handleLoadData = () => {
        let qparams = formQueryString();

        let apiURL;
        if (reportType === 'process') {
            apiURL = processReportApiURL;
        } else if (reportType === 'workflow') {
            apiURL = workflowReportApiURL;
        } else if (reportType === 'classification') {
            apiURL = classificationReportApiURL;
        } else if (reportType === 'enVision') {
            apiURL = envisionReportApiURL;
        } else if (reportType === 'core') {
            apiURL = coreReportApiURL;
        }

        // console.log("load url ", apiURL);

        fetchData(apiURL, qparams);

    };

    const handleChange = (event) => {
        setReportType(event.target.value);
    };

    const showCalculatedDateInstructions = () => {
        setOpenCalculatedDateDialog(true);
    };

    const showDateRangeInstructions = () => {
        setOpenReportRangeDialog(true);
    };

    const downloadReport = () => {
        let downloadApiURL = getDownloadApiURL();

        let qparams = formQueryString();

        if (qparams) {
            let message = `Downloading document ${reportType} report ...`;

            setNotify({
                isOpen: true,
                message: message,
                severity: 'info'
            });

            axios({
                method: "get", url: downloadApiURL + qparams,
                headers: { "insightUserId": getUser(), "companyId": getCompanyId(), "Authorization": getToken() },
                responseType: 'blob'
            })
                .then((response) => {

                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    const filename = response.headers['content-disposition'].split('filename=')[1].replace(/"/g, "");
                    link.download = !!filename ? filename : 'report.csv';
                    link.click();
                });

        } else {
            console.log("invalid query parameters for document report download", qparams);

            let message = `Invalid query parameters for ${reportType} document report download`;

            setNotify({
                isOpen: true,
                alertTitle: 'Request failed',
                message: message,
                severity: 'error'
            });
        }

    };

    const getContainerCss = () => {
        let containerCss;

        if (reportType === 'process') {
            containerCss = 'genericContainer1';
        } else if (reportType === 'workflow') {
            containerCss = 'genericContainer2';
        } else if (reportType === 'classification') {
            containerCss = 'genericContainer3';
        } else if (reportType === 'enVision') {
            containerCss = 'genericContainer1';
        } else if (reportType === 'core') {
            containerCss = 'genericContainer1';
        }

        return containerCss;
    };

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <div id="filterContainer" className='filterBox'>
                    <div className="filterContainer">
                        <div className="filter-actions-container">
                            <div id="content-left" className="filter-content-left">
                                <div id="filterTitle" className="filterTitle">Filters</div>
                            </div>
                        </div>

                        <FormControl>
                            <RadioGroup
                                row
                                aria-labelledby="report type radio buttons"
                                name="report-type-radio-buttons-group"
                                value={reportType}
                                onChange={handleChange}
                            >
                                <FormControlLabel value="process" control={<Radio size="small" />} label="CSP Process" />
                                <FormControlLabel value="workflow" control={<Radio size="small" />} label="Workflow" />
                                <FormControlLabel value="classification" control={<Radio size="small" />} label="Classification" />
                                <FormControlLabel value="enVision" control={<Radio size="small" />} label="enVision Process" />
                                <FormControlLabel value="core" control={<Radio size="small" />} label="Core Process" />
                            </RadioGroup>
                        </FormControl>

                        <div className="dateRangeContainer">
                            <div className="date-content-left">
                                <div className="datePickerContainer">
                                    <div className="calculatedDateLabel">Calculated Date</div>
                                    <div className="calculatedDatePicker">
                                        <DatePicker id="calculateDatePicker" inputRef={calculatedDateRef} value={calculatedDate}
                                            onChange={(newValue) => {
                                                setCalculatedDate(newValue);
                                            }}
                                            renderInput={(params) => <TextField id="calculatedDate" size="small"
                                                {...params}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    placeholder: "YYYY-MM-DD"
                                                }}
                                                sx={{
                                                    '& .MuiInputBase-root': {
                                                        width: '150px'
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        padding: '2px 0px 2px 14px',
                                                        fontFamily: 'Roboto',
                                                        fontStyle: 'italic',
                                                        fontWeight: '400',
                                                        fontSize: '14px',
                                                        lineHeight: '140%',
                                                        letterSpacing: '0.005em',
                                                        color: '#666663'
                                                    }
                                                }} />}
                                            inputFormat="YYYY-MM-DD"
                                        />
                                    </div>
                                </div>

                                <IconButton sx={{ paddingLeft: '0px' }} aria-label="calculated date instructions" onClick={showCalculatedDateInstructions}>
                                    <InfoIcon />
                                </IconButton>
                            </div>

                            <div className="reportDateRangeContainer">
                                <div className="toReportRangeContainer">
                                    <div className="reportDateRangeLabel">Report Date Range</div>
                                    <DatePicker id="fromDatePicker" inputRef={fromDateRef} value={fromDate}
                                        onChange={(newValue) => {
                                            setFromDate(newValue);
                                        }}
                                        renderInput={(params) => <TextField id="fromDate" size="small" {...params}
                                            inputProps={{
                                                ...params.inputProps,
                                                placeholder: "YYYY-MM-DD"
                                            }}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    width: '150px'
                                                },
                                                '& .MuiInputBase-input': {
                                                    padding: '2px 0px 2px 14px',
                                                    fontFamily: 'Roboto',
                                                    fontStyle: 'italic',
                                                    fontWeight: '400',
                                                    fontSize: '14px',
                                                    lineHeight: '140%',
                                                    letterSpacing: '0.005em',
                                                    color: '#666663'
                                                }
                                            }} />}
                                        inputFormat="YYYY-MM-DD"
                                    />

                                </div>


                                <div className="toDateLabel">To</div>
                                <div className="toDatePicker">
                                    <DatePicker id="toDatePicker" inputRef={toDateRef} value={toDate}
                                        onChange={(newValue) => {
                                            setToDate(newValue);
                                        }}
                                        renderInput={(params) => <TextField id="toDate" size="small" {...params} inputProps={{
                                            ...params.inputProps,
                                            placeholder: "YYYY-MM-DD"
                                        }}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    width: '150px'
                                                },
                                                '& .MuiInputBase-input': {
                                                    padding: '2px 0px 2px 14px',
                                                    fontFamily: 'Roboto',
                                                    fontStyle: 'italic',
                                                    fontWeight: '400',
                                                    fontSize: '14px',
                                                    lineHeight: '140%',
                                                    letterSpacing: '0.005em',
                                                    color: '#666663',
                                                }
                                            }} />}
                                        inputFormat="YYYY-MM-DD"
                                    />
                                </div>

                                <IconButton sx={{ paddingLeft: '0px' }} aria-label="date range instructions" onClick={showDateRangeInstructions}>
                                    <InfoIcon />
                                </IconButton>
                            </div>
                        </div>

                    </div>
                </div>

                <div className={getContainerCss()}>
                    <Button variant='contained' size="small" onClick={handleLoadData}
                        sx={{
                            lineHeight: 'normal',
                            textTransform: 'none',
                            background: '#82C027',
                            color: '#333330',
                            '&:hover': {
                                backgroundColor: '#82C027',
                                boxShadow: 'none',
                            }
                        }}>Load</Button>



                    <div id="downloadButtonSection" className="downloadButtonSection">
                        <Tooltip title="Download report based on filter criteria" placement="top" arrow>
                            <Button variant="text" startIcon={<FileDownloadOutlinedIcon sx={{ fill: '#6F7687' }} />} onClick={() => downloadReport()} sx={{ textTransform: 'none' }}>
                                Download Report
                            </Button>
                        </Tooltip>
                    </div>

                </div>

                <DraggableDialog
                    open={openInfoDialog}
                    onClose={handleInfoDialogClose}
                    title={filterInstructionsDialogTitle}
                    content={<div style={{ whiteSpace: 'pre', fontFamily: 'Roboto', fontSize: '14px' }}>{filterInstructionsTextContent2}</div>}
                />

                <DraggableDialog
                    open={openCalculatedDateDialog}
                    onClose={handleCalculatedDateDialogClose}
                    title={calculatedDateInstructionsDialogTitle}
                    content={calculatedDateInstructionsTextContent}
                />

                <DraggableDialog
                    open={openReportRangeInfoDialog}
                    onClose={handleReportRangeDialogClose}
                    title={reportDateRangeInstructionsDialogTitle}
                    content={reportDateRangeInstructionsTextContent}
                />

            </LocalizationProvider>
        </>
    );

}

export default ReportFilter;